/**
 * Robust clipboard copy with fallback.
 *
 * `navigator.clipboard.writeText` is the modern API but fails in several
 * common dev-time situations:
 *  - Page loaded inside an iframe without `clipboard-write` permission
 *  - Document not focused (e.g. devtools has focus)
 *  - Some browsers still reject it even on localhost if the page was just
 *    programmatically navigated without a real user gesture
 *
 * Falls back to the legacy `execCommand('copy')` approach — deprecated but
 * still works basically everywhere. Together this gives us the widest
 * compatibility.
 *
 * Returns true on success, false on failure. Callers decide the toast.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // 1) Modern API — try first.
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to legacy path.
  }

  // 2) Legacy fallback — a hidden textarea + execCommand.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Keep it off-screen but selectable. `readonly` prevents mobile keyboards.
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.width = '1px';
    ta.style.height = '1px';
    ta.style.padding = '0';
    ta.style.border = 'none';
    ta.style.outline = 'none';
    ta.style.boxShadow = 'none';
    ta.style.background = 'transparent';
    ta.style.opacity = '0';
    document.body.appendChild(ta);

    // Preserve selection if the user had something selected.
    const selection = document.getSelection();
    const prevRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);

    // Deprecated but widely supported — use a local to avoid TS deprecation noise.
    const exec = (document as unknown as { execCommand: (c: string) => boolean }).execCommand;
    const ok = typeof exec === 'function' ? exec.call(document, 'copy') : false;

    document.body.removeChild(ta);

    if (prevRange && selection) {
      selection.removeAllRanges();
      selection.addRange(prevRange);
    }
    return ok;
  } catch {
    return false;
  }
}
